import type { AlertColor } from '@mui/material/Alert';
import type React from 'react';
import { Button } from '../button';
import { MuiAlert, MuiAlertTitle } from '../mui-alert';
import { Stack } from '../stack';
import { Typography } from '../typography';

type EmptyStateWarning = {
	/**
	 * The title displayed in the alert header.
	 */
	title?: string;
	/**
	 * The body text of the alert.
	 */
	description?: string;
	/**
	 * The severity level of the alert.
	 */
	severity?: AlertColor;
};

type EmptyStateSize = 'xlarge' | 'large' | 'medium' | 'small';

const IMAGE_WIDTH: Record<EmptyStateSize, string> = {
	xlarge: '600px',
	large: '330px',
	medium: '280px',
	small: '110px'
};

/**
 * The available empty state image names.
 */
type EmptyStateImage = 'NoData' | 'EmptyProfile' | 'CirclesOrganise' | 'RecordNotFound';

const IMAGE_MAP: Record<EmptyStateImage, string> = {
	NoData: 'https://static.sfox.au/images/system_no-data.svg',
	EmptyProfile: 'https://static.sfox.au/images/system_empty-profile.svg',
	CirclesOrganise: 'https://static.sfox.au/images/circles_organise.svg',
	RecordNotFound: 'https://static.sfox.au/images/system_missing-record.svg'
};

export type EmptyStateProps = {
	/**
	 * Controls the width of the empty state image.
	 * @default 'large'
	 */
	size?: EmptyStateSize;
	/**
	 * The main heading of the empty state.
	 */
	title: string;
	/**
	 * The descriptive body text shown below the title.
	 */
	description: string;
	/**
	 * The name of the SVG image to display above the title.
	 */
	image?: EmptyStateImage;
	/**
	 * Optional warning / info alert shown below the description.
	 */
	warning?: EmptyStateWarning;
	/**
	 * URL opened in a new tab when the link button is clicked.
	 */
	link?: string;
	/**
	 * Label for the optional external link button.
	 */
	linkButtonTitle?: string;
	/**
	 * Additional action buttons rendered alongside the link button.
	 */
	extraButtons?: React.ReactNode;
};

export const EmptyState = ({ size = 'large', title, description, image, warning, link, linkButtonTitle, extraButtons }: EmptyStateProps) => (
	<Stack direction="column" justifyContent="center" style={{ height: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
		{image ? (
			<img src={IMAGE_MAP[image]} alt={title ?? ''} style={{ alignSelf: 'center', marginBottom: '1rem', width: IMAGE_WIDTH[size] }} />
		) : null}
		<Stack direction="column" alignItems="center" gap={0}>
			<Typography variant="h4">{title}</Typography>
			<Typography variant="body1" style={{ textAlign: 'center', marginBottom: '1rem' }}>
				{description}
			</Typography>
			{warning ? (
				<MuiAlert severity={warning.severity ?? 'info'}>
					{warning.title ? <MuiAlertTitle>{warning.title}</MuiAlertTitle> : null}
					{warning.description ?? null}
				</MuiAlert>
			) : null}
			{(link ?? extraButtons) ? (
				<Stack direction="row" justifyContent="center" alignItems="center" gap={1}>
					{link ? (
						<Button endIconName="OpenInNew" size="medium" onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}>
							{linkButtonTitle ?? 'Learn more'}
						</Button>
					) : null}
					{extraButtons ?? null}
				</Stack>
			) : null}
		</Stack>
	</Stack>
);
